import { tokenService } from '@/services/tokenService'
import { finalizeLocalSessionEnd } from '@/lib/sessionCleanup'
import axios from 'axios'
import { BASE_URL, API_KEY, CHANNEL_TYPE, DEBUG_MODE } from '@/config/api'

/**
 * GraphQL Auth Reason types as defined in the documentation
 */
export type GraphQLAuthReason =
  | 'TOKEN_EXPIRED'
  | 'TOKEN_INVALID'
  | 'TOKEN_MISSING'
  | 'TOKEN_BLACKLISTED'
  | 'TOKEN_NOT_VALID'
  | 'USER_NOT_FOUND'
  | 'USER_INACTIVE'
  | 'API_KEY_ONLY'

/**
 * GraphQL Auth Extension interface
 */
export interface GraphQLAuthExtension {
  isAuthenticated: boolean
  userId: string | null
  reason: GraphQLAuthReason | null
  message: string | null
  tokenExpiresAt: string | null
}

/**
 * Check if the auth extension indicates we should attempt token refresh
 */
export function shouldAttemptRefresh(
  authExtension: GraphQLAuthExtension | undefined | null
): boolean {
  if (!authExtension) {
    return false
  }

  // Attempt refresh if token is expired
  return (
    !authExtension.isAuthenticated &&
    authExtension.reason === 'TOKEN_EXPIRED'
  )
}

/**
 * Check if the response indicates an authentication error
 */
export function isAuthenticationError(
  errors: Array<{ message: string; extensions?: Record<string, unknown> }> | undefined,
  extensions?: { auth?: GraphQLAuthExtension } | undefined
): boolean {
  // Check extensions.auth first (more reliable)
  if (extensions?.auth) {
    const auth = extensions.auth
    return (
      !auth.isAuthenticated &&
      auth.reason !== null &&
      auth.reason !== 'API_KEY_ONLY'
    )
  }

  // Fallback to checking error messages
  if (errors && errors.length > 0) {
    const errorMessages = errors.map(e => e.message.toLowerCase())
    return errorMessages.some(
      msg =>
        msg.includes('token') ||
        msg.includes('authentication') ||
        msg.includes('unauthorized') ||
        msg.includes('not authenticated')
    )
  }

  return false
}

/**
 * Refresh authentication token
 * Returns the new access token if successful, null otherwise
 */
export async function refreshAuthToken(): Promise<string | null> {
  const refreshToken = await tokenService.getRefreshToken()
  if (!refreshToken) {
    if (DEBUG_MODE) {
      console.log('[GraphQL Auth] No refresh token available')
    }
    return null
  }

  try {
    const response = await axios.post(
      `${BASE_URL}/api/auth/token/refresh/`,
      { refresh: refreshToken },
      {
        headers: {
          'X-API-Key': API_KEY,
          'X-WTD-Channel': CHANNEL_TYPE,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      }
    )

    const { access, refresh: newRefresh } = response.data
    await tokenService.saveTokens(access, newRefresh ?? refreshToken)

    if (DEBUG_MODE) {
      console.log('[GraphQL Auth] Token refresh successful')
    }

    return access
  } catch (error) {
    if (DEBUG_MODE) {
      console.error('[GraphQL Auth] Token refresh failed:', error)
    }
    
    if (typeof window !== 'undefined') {
      await finalizeLocalSessionEnd('token_refresh_failed')
    } else {
      await tokenService.clearTokens()
    }

    return null
  }
}

/**
 * Handle GraphQL authentication error
 * Attempts to refresh token and returns whether retry should be attempted
 */
export async function handleGraphQLAuthError(
  authExtension: GraphQLAuthExtension | undefined | null,
  errors?: Array<{ message: string; extensions?: Record<string, unknown> }> | undefined,
  extensions?: { auth?: GraphQLAuthExtension } | undefined
): Promise<{ shouldRetry: boolean; newToken: string | null }> {
  // Check if we should attempt refresh
  if (!shouldAttemptRefresh(authExtension || extensions?.auth)) {
    // Check if it's an auth error that we can't recover from
    if (isAuthenticationError(errors, extensions)) {
      const auth = authExtension || extensions?.auth
      if (auth && auth.reason && auth.reason !== 'TOKEN_EXPIRED') {
        // Non-recoverable auth error (invalid token, user not found, etc.)
        if (DEBUG_MODE) {
          console.log('[GraphQL Auth] Non-recoverable auth error:', auth.reason)
        }
        
        if (typeof window !== 'undefined') {
          await finalizeLocalSessionEnd(String(auth.reason))
        } else {
          await tokenService.clearTokens()
        }
      }
    }
    
    return { shouldRetry: false, newToken: null }
  }

  // Attempt token refresh
  const newToken = await refreshAuthToken()
  
  if (newToken) {
    return { shouldRetry: true, newToken }
  }

  return { shouldRetry: false, newToken: null }
}
